import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag, Heart, Eye, ArrowRight, X, Star, Sparkles,
  ArrowDown, Droplets, Wind, Zap, Sun, Moon, Clock, Radio
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BASE_URL, ENDPOINTS, getImageUrl, addToWishlist } from '../apiConfig';
import heroBg from '../assets/1d511ceb-a632-4d1d-8887-2b8c7a6994c3.jpg';
import oudBg from '../assets/2bc9bf8d-eb81-4664-b1ff-0f286998c3f2.jpg';
import floralBg from '../assets/ee4137c9-7ccf-400c-9678-9044856007ab.jpg';
import freshBg from '../assets/about-hero.jpg';
import generalBg from '../assets/44d94a64-3219-4155-abb3-bc32774c8a88.jpg';
import './CollectionsPage.css';

gsap.registerPlugin(ScrollTrigger);

// ── SCENT METADATA ENGINE ──
const getScentMeta = (name = '') => {
  const lower = name.toLowerCase();
  if (lower.includes('oud') || lower.includes('noir')) return {
    top: 'Bergamot', heart: 'Saffron', base: 'Agarwood', intensity: 92,
    longevity: '12+ Hours', sillage: 'Extremely Strong', time: 'Night',
    mood: 'Powerful', season: 'Winter', scent_family: 'Oriental',
    occasions: ['Date Night', 'Evening Event', 'Luxury Occasion'],
    limited: true,
    perfumer_note: '"A journey through ancient trade routes — bold, mysterious, and unforgettable."',
    layer_with: 'Accento',
  };
  if (lower.includes('rose') || lower.includes('floral') || lower.includes('bomb')) return {
    top: 'Pink Pepper', heart: 'Turkish Rose', base: 'Musk', intensity: 68,
    longevity: '8 Hours', sillage: 'Moderate', time: 'All Day',
    mood: 'Romantic', season: 'Spring', scent_family: 'Floral',
    occasions: ['Wedding', 'Date Night', 'Brunch'],
    limited: false,
    perfumer_note: '"Inspired by the rose gardens of Grasse — feminine, radiant, and timeless."',
    layer_with: 'BombShell',
  };
  if (lower.includes('fresh') || lower.includes('summer') || lower.includes('aqua')) return {
    top: 'Lemon', heart: 'Sea Salt', base: 'White Cedar', intensity: 45,
    longevity: '6 Hours', sillage: 'Intimate', time: 'Day',
    mood: 'Refreshing', season: 'Summer', scent_family: 'Citrus',
    occasions: ['Office', 'Casual', 'Sports'],
    limited: false,
    perfumer_note: '"Bottled sunshine — the feeling of ocean breeze on a warm afternoon."',
    layer_with: null,
  };
  return {
    top: 'Bergamot', heart: 'Jasmine', base: 'Amber', intensity: 75,
    longevity: '10 Hours', sillage: 'Noticeable', time: 'Night',
    mood: 'Sophisticated', season: 'Autumn', scent_family: 'Woody',
    occasions: ['Evening Event', 'Date Night', 'Formal'],
    limited: Math.random() > 0.6,
    perfumer_note: '"A masterclass in balance — where warmth meets quiet confidence."',
    layer_with: 'Accento',
  };
};

const SEASON_CONFIG = {
  Summer:  { label: '☀ Summer', color: '#F59E0B' },
  Winter:  { label: '❄ Winter', color: '#60A5FA' },
  Spring:  { label: '🌿 Spring', color: '#34D399' },
  Autumn:  { label: '🍂 Autumn', color: '#F97316' },
};

const SCENT_FAMILY_CONFIG = {
  Oriental: { emoji: '🌙', color: '#9333EA' },
  Floral:   { emoji: '🌸', color: '#EC4899' },
  Citrus:   { emoji: '🍋', color: '#EAB308' },
  Woody:    { emoji: '🪵', color: '#A16207' },
};

const CollectionsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [wishlist, setWishlist] = useState(() => {
    try { return JSON.parse(localStorage.getItem('wishlistItems') || '[]'); }
    catch { return []; }
  });

  const navigate = useNavigate();
  const pageRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [catRes, prodRes] = await Promise.all([
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_CATEGORIES}`, { headers }),
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}`, { headers })
      ]);
      const catData = await catRes.json();
      const prodData = await prodRes.json();

      const parsedCats = Array.isArray(catData.data) ? catData.data : (Array.isArray(catData) ? catData : []);
      const parsedProds = Array.isArray(prodData.data) ? prodData.data : (Array.isArray(prodData) ? prodData : []);

      setCategories(parsedCats);
      setProducts(parsedProds);
    } catch (err) {
      console.error('Error fetching collections:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && categories.length > 0) {
      const ctx = gsap.context(() => {
        // Hero staggered entrance
        gsap.from('.universe-hero > *', { y: 60, opacity: 0, duration: 1.5, stagger: 0.2, ease: 'power4.out' });

        gsap.utils.toArray('.collection-universe-section').forEach(section => {
          const content = section.querySelector('.universe-content');
          if (content) {
            gsap.from(content, { 
              y: 50, 
              opacity: 0, 
              duration: 1.2, 
              scrollTrigger: { trigger: section, start: 'top 85%' } 
            });
          }
        });
      }, pageRef);
      return () => ctx.revert();
    }
  }, [loading, categories]);

  const toggleWishlist = (product) => {
    const isExisting = wishlist.find(i => i._id === product._id);
    const updated = isExisting
      ? wishlist.filter(i => i._id !== product._id)
      : [...wishlist, product];
    if (!isExisting) {
      addToWishlist(product._id || product.id).catch(console.error);
    }
    setWishlist(updated);
    localStorage.setItem('wishlistItems', JSON.stringify(updated));
    window.dispatchEvent(new Event('wishlistUpdated'));
  };

  const handleAddToCart = (product) => {
    const cartList = JSON.parse(localStorage.getItem('cartItems') || '[]');
    localStorage.setItem('cartItems', JSON.stringify([...cartList, product]));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const getCollectionBg = (name = '') => {
    const lower = name.toLowerCase();
    if (lower.includes('oud') || lower.includes('oriental')) return oudBg;
    if (lower.includes('floral') || lower.includes('rose')) return floralBg;
    if (lower.includes('summer') || lower.includes('fresh')) return freshBg;
    return generalBg;
  };

  if (loading) return (
    <div className="collections-loading-screen">
      <div className="loading-logo">ESCENTRUM</div>
      <div className="loading-bar-container"><div className="loading-bar"></div></div>
      <p>AWAKENING THE SENSES...</p>
    </div>
  );

  const sp = selectedProduct ? getScentMeta(selectedProduct.name) : null;

  return (
    <div className="collections-universe-v2" ref={pageRef}>

      {/* ── HERO ── */}
      <section className="universe-hero">
        <div className="hero-overlay"></div>
        <img src={heroBg} alt="Escentrum Collection" className="hero-bg-img" />
        <div className="hero-center">
          <span className="hero-tag">THE ART OF FRAGRANCE</span>
          <h1 className="coll-universe-title">Discover Your <span>Universe</span></h1>
          <p className="hero-sub">Explore our thematic collections — each a distinct olfactory journey crafted for the extraordinary.</p>
          <div className="scroll-hint"><span>SCROLL TO EXPLORE</span><ArrowDown size={16} /></div>
        </div>
      </section>

      {/* ── COLLECTION SECTIONS ── */}
      <div className="universe-sections-container">
        {Array.isArray(categories) && categories.map((cat, index) => {
          const catProducts = products.filter(p => {
            const pCat = p.category || p.categoryId;
            const pCatId = typeof pCat === 'object' ? pCat?._id : pCat;
            const targetCatId = typeof cat === 'object' ? cat?._id : cat;
            return pCatId === targetCatId;
          }).slice(0, 4);
          if (catProducts.length === 0) return null;

          return (
            <section key={cat._id} className={`collection-universe-section ${index % 2 === 0 ? 'even' : 'odd'}`}>

              {/* ── TOP ROW: Image + Text side by side ── */}
              <div className="universe-content">
                
                {/* IMAGE PANEL */}
                <div className="parallax-bg-wrapper">
                  <div className="parallax-bg-inner">
                    <img src={getCollectionBg(cat.name)} alt={cat.name} className="parallax-bg" />
                  </div>
                  <div className="section-overlay"></div>
                </div>

                {/* TEXT PANEL */}
                <div className="universe-text">
                  <span className="collection-index">0{index + 1}</span>
                  <h2 className="collection-name">{cat.name} <span>Collection</span></h2>
                  <p className="collection-description">
                    {cat.description || `Experience the essence of ${cat.name}. Masterfully blended notes designed to evoke deep emotions and lasting memories.`}
                  </p>
                  <div className="coll-ingredient-box">
                    <Sparkles size={16} color="#d4af37" />
                    <span>PROVENANCE: {cat.name.toLowerCase().includes('oud') ? 'Saffron & Rare Cambodian Oud' : 'Hand-picked Roses from Grasse, France'}</span>
                  </div>
                  <button className="explore-coll-btn" onClick={() => navigate('/shop')}>
                    VIEW FULL COLLECTION <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              {/* ── BOTTOM ROW: Product Cards ── */}
              <div className="universe-products-preview">
                {catProducts.map(product => {
                  const productImage = getImageUrl(product.images?.[0]);
                  const hoverImage = product.images?.[1] ? getImageUrl(product.images[1]) : productImage;
                  const meta = getScentMeta(product.name);
                  const season = SEASON_CONFIG[meta.season] || SEASON_CONFIG.Autumn;
                  const scentFam = SCENT_FAMILY_CONFIG[meta.scent_family] || SCENT_FAMILY_CONFIG.Woody;

                  return (
                    <div key={product._id} className="preview-card-v2">
                      {/* Visual */}
                      <div className="preview-visual" onClick={() => {
                        navigate(`/product/${product._id || product.id}`);
                      }}>
                        {/* ── LIMITED EDITION BADGE ── */}
                        {meta.limited && <div className="limited-badge">✦ LIMITED</div>}

                        {/* ── MOOD TAG ── */}
                        <div className="mood-tag">{meta.mood}</div>

                        {/* ── SEASON BADGE ── */}
                        <div className="season-badge" style={{ color: season.color }}>
                          {season.label}
                        </div>

                        <img src={productImage} alt={product.name} className={`preview-img ${product.images?.[1] ? 'primary-img' : ''}`} />
                        {product.images?.[1] && (
                          <img src={hoverImage} alt={`${product.name} hover`} className="preview-img hover-img" />
                        )}
                        <div className="preview-hover"><Eye size={20} /></div>
                      </div>

                      {/* Info */}
                      <div className="preview-info">
                        {/* ── SCENT FAMILY ── */}
                        <div className="scent-family-tag" style={{ background: scentFam.color + '22', color: scentFam.color }}>
                          {scentFam.emoji} {meta.scent_family}
                        </div>

                        <h4>{product.name}</h4>

                        {/* ── OCCASION GUIDE ── */}
                        <div className="occasion-row">
                          {meta.occasions.slice(0, 2).map(occ => (
                            <span key={occ} className="occasion-chip">{occ}</span>
                          ))}
                        </div>

                        {/* Intensity Mini Bar */}
                        <div className="mini-intensity">
                          <div className="intensity-bar">
                            <div className="intensity-fill" style={{ width: `${meta.intensity}%` }}></div>
                          </div>
                          <span>{meta.intensity > 80 ? 'HIGH' : 'MODERATE'} INTENSITY</span>
                        </div>

                        <div className="preview-footer">
                          <span className="price">
                            Rs. {(() => {
                              const v = product.variants?.find(v => v.isDefault) || product.variants?.[0] || {};
                              const pr = v.finalPrice || v.price || product.price || 0;
                              return pr.toLocaleString();
                            })()}
                          </span>
                          <button className="preview-cart-btn" onClick={(e) => { e.stopPropagation(); handleAddToCart(product); }}>
                            <ShoppingBag size={14} />
                            <span>ADD TO CART</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* ── CONCLUSION ── */}
      <section className="universe-conclusion">
        <div className="conclusion-content">
          <h3>Beyond the Bottle</h3>
          <p>Every collection is a chapter in our brand's history. We don't just create perfumes; we capture moments in time.</p>
          <div className="signature-logo">Escentrum Private</div>
        </div>
      </section>

      {/* ── QUICK VIEW MODAL (Full Luxury) ── */}
      {selectedProduct && sp && (
        <div className="quick-view-overlay-v5" onClick={() => setSelectedProduct(null)}>
          <div className="quick-view-card-v5" onClick={e => e.stopPropagation()}>
            <button className="modal-close-v5" onClick={() => setSelectedProduct(null)}><X size={28} /></button>
            <div className="modal-grid-v5">

              {/* Image */}
              <div className="modal-visual-v5">
                <img
                  src={getImageUrl(selectedProduct.images?.[0])}
                  alt={selectedProduct.name}
                />
                {/* Badges on modal image */}
                {sp.limited && <div className="modal-limited-badge">✦ LIMITED EDITION</div>}
                <div className="modal-season-badge" style={{ color: (SEASON_CONFIG[sp.season] || SEASON_CONFIG.Autumn).color }}>
                  {(SEASON_CONFIG[sp.season] || SEASON_CONFIG.Autumn).label}
                </div>
              </div>

              {/* Details */}
              <div className="modal-content-v5">
                <div className="m-trust-v5"><Star size={12} fill="#d4af37" color="#d4af37" /><span>CERTIFIED MASTERPIECE</span></div>
                <span className="m-brand">{selectedProduct.brand || 'Escentrum Private'}</span>
                <h2 className="m-title">{selectedProduct.name}</h2>
                <div className="m-price">Rs. {selectedProduct.price?.toLocaleString()}</div>

                {/* ── SCENT FAMILY & OCCASIONS ── */}
                <div className="modal-meta-row">
                  <div
                    className="modal-scent-family"
                    style={{
                      background: (SCENT_FAMILY_CONFIG[sp.scent_family] || SCENT_FAMILY_CONFIG.Woody).color + '22',
                      color: (SCENT_FAMILY_CONFIG[sp.scent_family] || SCENT_FAMILY_CONFIG.Woody).color
                    }}
                  >
                    {(SCENT_FAMILY_CONFIG[sp.scent_family] || SCENT_FAMILY_CONFIG.Woody).emoji} {sp.scent_family} Family
                  </div>
                  <div className="modal-occasions">
                    {sp.occasions.map(occ => (
                      <span key={occ} className="modal-occasion-chip">{occ}</span>
                    ))}
                  </div>
                </div>

                {/* ── PERFUMER'S NOTE ── */}
                <div className="perfumer-note">
                  <span className="perfumer-label">✦ PERFUMER'S NOTE</span>
                  <p>{sp.perfumer_note}</p>
                </div>

                {/* ── PERFORMANCE ── */}
                <div className="performance-grid">
                  <div className="perf-item"><Clock size={15} color="#d4af37" /><div className="perf-text"><span>LONGEVITY</span><strong>{sp.longevity}</strong></div></div>
                  <div className="perf-item"><Radio size={15} color="#d4af37" /><div className="perf-text"><span>SILLAGE</span><strong>{sp.sillage}</strong></div></div>
                  <div className="perf-item">
                    {sp.time === 'Night' ? <Moon size={15} color="#d4af37" /> : <Sun size={15} color="#d4af37" />}
                    <div className="perf-text"><span>BEST WORN</span><strong>{sp.time}</strong></div>
                  </div>
                  <div className="perf-item"><Sparkles size={15} color="#d4af37" /><div className="perf-text"><span>MOOD</span><strong>{sp.mood}</strong></div></div>
                </div>

                {/* ── SCENT PYRAMID ── */}
                <div className="olfactory-pyramid">
                  <h5 className="section-subtitle">SCENT ARCHITECTURE</h5>
                  <div className="pyramid-grid">
                    <div className="pyramid-item"><Wind size={16} color="#d4af37" /><div className="note-text"><span>TOP</span><strong>{sp.top}</strong></div></div>
                    <div className="pyramid-item"><Droplets size={16} color="#d4af37" /><div className="note-text"><span>HEART</span><strong>{sp.heart}</strong></div></div>
                    <div className="pyramid-item"><Zap size={16} color="#d4af37" /><div className="note-text"><span>BASE</span><strong>{sp.base}</strong></div></div>
                  </div>
                </div>

                {/* ── INTENSITY ── */}
                <div className="intensity-scale-box">
                  <div className="intensity-header"><span>INTENSITY LEVEL</span><strong>{sp.intensity}%</strong></div>
                  <div className="intensity-full-bar"><div className="intensity-level" style={{ width: `${sp.intensity}%` }}></div></div>
                </div>

                {/* ── LAYER PAIRING ── */}
                {sp.layer_with && (
                  <div className="layer-pairing">
                    <span>✦ PAIRS BEAUTIFULLY WITH</span>
                    <strong>{sp.layer_with}</strong>
                  </div>
                )}

                {/* ── ACTIONS ── */}
                <div className="m-actions">
                  <button className="m-add-btn" onClick={() => { handleAddToCart(selectedProduct); setSelectedProduct(null); }}>RESERVE NOW</button>
                  <button className="m-view-btn" onClick={() => { navigate(`/product/${selectedProduct._id}`); setSelectedProduct(null); }}>VIEW DETAILS</button>
                  <button className="m-wish-btn" onClick={() => toggleWishlist(selectedProduct)}>
                    <Heart size={20} fill={wishlist.some(i => i._id === selectedProduct._id) ? '#d4af37' : 'none'} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionsPage;
